import { solveLevel } from './solver'
import type { LevelMeta, Difficulty } from '@/game/levels'
import { BIOME_ORDER, type BiomeId } from '@/styles/theme'

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function nextSeed(seed: number): number {
  let h = seed >>> 0
  h = Math.imul(h ^ (h >>> 16), 2246822507)
  h = Math.imul(h ^ (h >>> 13), 3266489909)
  return (h ^ (h >>> 16)) >>> 0 || 1
}

interface DifficultyParams {
  minSize: number
  maxSize: number
  wallDensity: number
  iceCount: number
  hazardCount: number
  enemyCount: number
  breakableCount: number
  useKeyDoor: boolean
  useTeleport: boolean
  useSwitch: boolean
  coinCount: number
  gemCount: number
  band: [number, number]
}

function paramsFor(difficulty: number): DifficultyParams {
  const d = Math.max(1, Math.min(6, Math.round(difficulty)))
  switch (d) {
    case 1:
      return { minSize: 4, maxSize: 5, wallDensity: 0.14, iceCount: 0, hazardCount: 0, enemyCount: 0, breakableCount: 0, useKeyDoor: false, useTeleport: false, useSwitch: false, coinCount: 3, gemCount: 0, band: [3, 6] }
    case 2:
      return { minSize: 5, maxSize: 6, wallDensity: 0.16, iceCount: 2, hazardCount: 0, enemyCount: 0, breakableCount: 0, useKeyDoor: false, useTeleport: false, useSwitch: false, coinCount: 3, gemCount: 0, band: [4, 8] }
    case 3:
      return { minSize: 6, maxSize: 7, wallDensity: 0.17, iceCount: 2, hazardCount: 2, enemyCount: 1, breakableCount: 0, useKeyDoor: false, useTeleport: false, useSwitch: false, coinCount: 4, gemCount: 1, band: [4, 10] }
    case 4:
      return { minSize: 7, maxSize: 8, wallDensity: 0.18, iceCount: 3, hazardCount: 3, enemyCount: 1, breakableCount: 2, useKeyDoor: true, useTeleport: false, useSwitch: false, coinCount: 4, gemCount: 1, band: [5, 12] }
    case 5:
      return { minSize: 7, maxSize: 8, wallDensity: 0.19, iceCount: 3, hazardCount: 4, enemyCount: 2, breakableCount: 2, useKeyDoor: true, useTeleport: true, useSwitch: true, coinCount: 5, gemCount: 1, band: [6, 14] }
    default:
      return { minSize: 8, maxSize: 9, wallDensity: 0.2, iceCount: 4, hazardCount: 5, enemyCount: 2, breakableCount: 2, useKeyDoor: true, useTeleport: true, useSwitch: true, coinCount: 5, gemCount: 2, band: [7, 16] }
  }
}

const DIFFICULTY_LABEL: Record<number, Difficulty> = {
  1: 'easy',
  2: 'easy',
  3: 'medium',
  4: 'hard',
  5: 'expert',
  6: 'expert',
}

const NAME_PREFIX: Record<BiomeId, string[]> = {
  forest: ['Mossy', 'Fern', 'Bramble', 'Willow', 'Dappled', 'Hollow'],
  cave: ['Echoing', 'Sunken', 'Crystal', 'Silent', 'Winding', 'Deep'],
  lava: ['Smoldering', 'Ashen', 'Molten', 'Cinder', 'Charred', 'Blistered'],
  ice: ['Frozen', 'Glacial', 'Frosted', 'Shivering', 'Polished', 'Pale'],
  magical: ['Shimmering', 'Warped', 'Astral', 'Prismatic', 'Whispered', 'Folded'],
  dungeon: ['Forsaken', 'Iron', 'Grim', 'Vaulted', 'Shadowed', 'Final'],
}

const NAME_SUFFIX = ['Crossing', 'Run', 'Passage', 'Detour', 'Gauntlet', 'Maze', 'Stretch', 'Descent', 'Drift', 'Circuit']

function buildName(biome: BiomeId, rng: () => number): string {
  const pre = NAME_PREFIX[biome]
  return `${pre[Math.floor(rng() * pre.length)]} ${NAME_SUFFIX[Math.floor(rng() * NAME_SUFFIX.length)]}`
}

interface Candidate {
  grid: string[]
  rows: number
  cols: number
}

interface SolvedCandidate {
  candidate: Candidate
  minSlides: number
  maxCoins: number
}

export function wallPlacementWeight(x: number, y: number, rows: number, cols: number): number {
  const edgeDistance = Math.min(x, y, cols - 1 - x, rows - 1 - y)
  const centralX = x >= Math.floor(cols * 0.33) && x <= Math.ceil(cols * 0.67) - 1
  const centralY = y >= Math.floor(rows * 0.33) && y <= Math.ceil(rows * 0.67) - 1
  if (centralX && centralY) return 0.25
  if (edgeDistance === 0) return 6
  if (edgeDistance === 1) return 3
  return 1
}

function buildCandidate(rng: () => number, p: DifficultyParams): Candidate {
  const rows = p.minSize + Math.floor(rng() * (p.maxSize - p.minSize + 1))
  const cols = p.minSize + Math.floor(rng() * (p.maxSize - p.minSize + 1))
  const cells: string[][] = Array.from({ length: rows }, () => Array.from({ length: cols }, () => '-'))

  const cellCount = rows * cols
  const minDist = Math.max(3, Math.floor((rows + cols) / 2))

  const startX = Math.floor(rng() * cols)
  const startY = Math.floor(rng() * rows)
  let endX = 0
  let endY = 0
  let tries = 0
  do {
    endX = Math.floor(rng() * cols)
    endY = Math.floor(rng() * rows)
    tries++
  } while (Math.abs(endX - startX) + Math.abs(endY - startY) < minDist && tries < 64)

  cells[startY][startX] = 'o'
  cells[endY][endX] = 'e'

  const placeRandom = (tile: string, count: number) => {
    let placed = 0
    let attempts = 0
    while (placed < count && attempts < cellCount * 4) {
      attempts++
      const candidates: { x: number; y: number; weight: number }[] = []
      let totalWeight = 0
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          if (cells[y][x] !== '-') continue
          const nearStart = Math.abs(x - startX) + Math.abs(y - startY) <= 1
          const nearEnd = Math.abs(x - endX) + Math.abs(y - endY) <= 1
          if ((tile === 'l' || tile === '~' || tile === 'x' || tile === 'd' || tile === 'b') && (nearStart || nearEnd)) continue
          const weight = tile === 'x' ? wallPlacementWeight(x, y, rows, cols) : 1
          candidates.push({ x, y, weight })
          totalWeight += weight
        }
      }
      if (candidates.length === 0) break
      let pick = rng() * totalWeight
      let chosen = candidates[candidates.length - 1]
      for (const candidate of candidates) {
        pick -= candidate.weight
        if (pick <= 0) {
          chosen = candidate
          break
        }
      }
      const { x, y } = chosen
      cells[y][x] = tile
      placed++
    }
    return placed
  }

  const wallCount = Math.max(2, Math.floor(cellCount * p.wallDensity))
  placeRandom('x', wallCount)

  if (p.iceCount > 0) placeRandom('s', 1 + Math.floor(rng() * p.iceCount))
  if (p.hazardCount > 0) {
    const lavaish = 1 + Math.floor(rng() * p.hazardCount)
    for (let i = 0; i < lavaish; i++) placeRandom(rng() < 0.5 ? 'l' : '~', 1)
  }
  if (p.enemyCount > 0) placeRandom('m', p.enemyCount)
  if (p.breakableCount > 0) placeRandom('b', 1 + Math.floor(rng() * p.breakableCount))

  if (p.useKeyDoor && rng() < 0.6) {
    const useSwitch = p.useSwitch && rng() < 0.4
    if (placeRandom('d', 1) === 1) {
      if (useSwitch) placeRandom('w', 1)
      else placeRandom('k', 1)
    }
  }

  if (p.useTeleport && rng() < 0.45) {
    placeRandom('t', 2)
  }

  placeRandom('p', p.coinCount)
  if (p.gemCount > 0) placeRandom('g', p.gemCount)

  return { grid: cells.map((r) => r.join('')), rows, cols }
}

function countTiles(grid: string[], tile: string): number {
  let n = 0
  for (const row of grid) for (const ch of row) if (ch === tile) n++
  return n
}

function solveCandidate(candidate: Candidate): SolvedCandidate | null {
  const solved = solveLevel(candidate.grid)
  if (!solved.solvable) return null
  return { candidate, minSlides: solved.minSlides, maxCoins: solved.maxCoins }
}

function repairedLevel(seed: number, difficulty: number, p: DifficultyParams): Candidate {
  const rows = Math.max(3, p.minSize)
  const cols = Math.max(5, Math.min(p.maxSize + 1, p.minSize + 2 + (seed % 2)))
  const cells: string[][] = Array.from({ length: rows }, () => Array.from({ length: cols }, () => '-'))
  const y = Math.floor(rows / 2)
  cells[y][0] = 'o'
  cells[y][cols - 1] = 'e'

  const collectibleSlots = Math.max(0, cols - 2)
  const coinCount = Math.min(p.coinCount, collectibleSlots)
  for (let i = 0; i < coinCount; i++) cells[y][1 + i] = 'p'

  if (p.gemCount > 0 && coinCount < collectibleSlots) {
    cells[y][1 + coinCount] = 'g'
  }

  const sideY = y === 0 ? rows - 1 : 0
  for (let x = 0; x < cols; x++) {
    if (x % 2 === 0) cells[sideY][x] = 'x'
  }

  if (difficulty >= 3 && rows > 3) {
    const hazardY = y === rows - 1 ? 1 : rows - 1
    for (let x = 1; x < cols - 1; x += 3) cells[hazardY][x] = x % 2 === 0 ? '~' : 'l'
    if (p.enemyCount > 0 && cols > 4) cells[hazardY][cols - 2] = 'm'
  }

  return { grid: cells.map((r) => r.join('')), rows, cols }
}

function generateLevelForBiome(seed: number, difficulty: number, biome: BiomeId): LevelMeta {
  const d = Math.max(1, Math.min(6, Math.round(difficulty)))
  const p = paramsFor(d)

  let currentSeed = seed >>> 0 || 1
  let best: SolvedCandidate | null = null

  const maxAttempts = 40
  for (let attempt = 0; attempt < maxAttempts * 3; attempt++) {
    const rng = mulberry32(currentSeed)
    const candidate = buildCandidate(rng, p)
    currentSeed = nextSeed(currentSeed)

    const solvedCandidate = solveCandidate(candidate)
    if (!solvedCandidate) continue

    const { minSlides } = solvedCandidate

    const inBand = minSlides >= p.band[0] && minSlides <= p.band[1]
    const wideBand = attempt >= maxAttempts && minSlides >= Math.max(2, p.band[0] - 2)

    if (inBand || wideBand) {
      best = solvedCandidate
      break
    }
    if (!best && minSlides >= 2) {
      best = solvedCandidate
    }
  }

  if (!best) {
    best = solveCandidate(repairedLevel(seed, d, p))
  }

  if (!best) {
    throw new Error(`Unable to generate solvable level for seed ${seed} difficulty ${difficulty}`)
  }

  const { candidate, minSlides, maxCoins } = best
  const nameRng = mulberry32(seed >>> 0 || 1)
  const coinCount = countTiles(candidate.grid, 'p')
  const gemCount = countTiles(candidate.grid, 'g')

  return {
    index: -1,
    name: buildName(biome, nameRng),
    biome,
    difficulty: DIFFICULTY_LABEL[d],
    grid: candidate.grid,
    rows: candidate.rows,
    cols: candidate.cols,
    stepTarget: minSlides + (d >= 4 ? 2 : 1),
    minSlides,
    timeTargetMs: 12000 + minSlides * 3500,
    minStars: 0,
    coinCount,
    gemCount,
    maxCoins,
  }
}

export function generateLevel(seed: number, difficulty: number): LevelMeta {
  const d = Math.max(1, Math.min(6, Math.round(difficulty)))
  return generateLevelForBiome(seed, d, BIOME_ORDER[d - 1])
}

export function generateBiomeLevel(seed: number, biome: BiomeId, difficulty: number): LevelMeta {
  return generateLevelForBiome(seed, difficulty, biome)
}

export const ENDLESS_INDEX_BASE = 1000
export const ENDLESS_BASE_SEED = 0x54484d45

export function endlessDifficulty(runIndex: number): number {
  return Math.max(1, Math.min(6, 1 + Math.floor(runIndex / 5)))
}

export function generateEndlessLevel(runIndex: number, baseSeed: number = ENDLESS_BASE_SEED): LevelMeta {
  const idx = Math.max(0, Math.floor(runIndex))
  let seed = baseSeed >>> 0 || 1
  for (let i = 0; i <= idx % 97; i++) seed = nextSeed(seed)
  seed = (seed ^ Math.imul(idx + 1, 2654435761)) >>> 0 || 1

  const level = generateLevel(seed, endlessDifficulty(idx))
  return {
    ...level,
    index: ENDLESS_INDEX_BASE + idx,
    name: `${level.name} ${idx + 1}`,
  }
}
